import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, GraduationCap, Award, Briefcase, Handshake, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { educationalPartners } from '../data/mock';

export default function AdvancementPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-navy-50 via-white to-navy-50/30">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-96 h-96 bg-navy-100/30 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-navy-500 mb-6">
              <Link to="/" className="hover:text-brand-green transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-500">Programs</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-navy-900 font-medium">Advancement</span>
            </nav>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy-100 border border-navy-200 mb-6">
              <TrendingUp className="w-4 h-4 text-brand-navy" />
              <span className="text-sm font-medium text-navy-700">Advancement Program</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6">
              Elevate Your Career:{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-navy to-navy-700">
                Growth Opportunities
              </span>
            </h1>
            <p className="text-lg text-navy-600 leading-relaxed">
              DC Men is more than just a community; it's a movement dedicated to empowering young men through self-development, education, and community engagement.
            </p>
          </div>
        </div>
      </section>

      {/* How We Advance */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">How We Advance</h2>
              <div className="space-y-4 text-navy-600 leading-relaxed">
                <p>
                  At DC Men in ECE, we are dedicated to advancing the careers of men of color in early childhood education. We provide opportunities for skill and knowledge development, career mobility, and professional recognition.
                </p>
                <p>
                  Our initiatives include higher-education counseling, scholarships, and a robust job board. Through regular workshops, networking sessions, and our annual conference, we equip educators with the tools and connections they need to succeed and lead, ultimately enriching the educational experiences of young children.
                </p>
              </div>
            </div>
            
            {/* Image */}
            <div className="relative">
              <div className="bg-gradient-to-br from-navy-50 to-gold-50 rounded-3xl p-4 shadow-xl overflow-hidden">
                <img 
                  src="/images/advancement-photo.jpg" 
                  alt="DC Men in ECE Advancement"
                  className="w-full h-72 object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Advancement Opportunities</h2>
            <p className="text-navy-300">Tools and resources to help you grow in your career.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-navy-800/50 border-navy-700/50 hover:border-brand-gold/30 transition-all duration-500">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-gold to-gold-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-gold-500/20">
                  <GraduationCap className="w-7 h-7 text-navy-900" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Professional Growth</h3>
                <p className="text-navy-300 text-sm">Continuous development opportunities</p>
              </CardContent>
            </Card>
            
            <Card className="bg-navy-800/50 border-navy-700/50 hover:border-brand-green/30 transition-all duration-500">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-green to-green-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/20">
                  <Handshake className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Prestigious Partners</h3>
                <p className="text-navy-300 text-sm">Partnerships with leading institutions</p>
              </CardContent>
            </Card>
            
            <Card className="bg-navy-800/50 border-navy-700/50 hover:border-brand-red/30 transition-all duration-500">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-red to-red-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/20">
                  <Briefcase className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Development Sessions</h3>
                <p className="text-navy-300 text-sm">Partner-led skill building workshops</p>
              </CardContent>
            </Card>
            
            <Card className="bg-navy-800/50 border-navy-700/50 hover:border-brand-gold/30 transition-all duration-500">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-navy to-navy-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-navy-500/20">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Recognizing Achievements</h3>
                <p className="text-navy-300 text-sm">Celebrating professional milestones</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Educational Partners */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">Our Educational Partners</h2>
            <p className="text-navy-600">We have established partnerships with esteemed institutions to offer the best in educational advancement.</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6">
            {educationalPartners.map((partner, index) => (
              <div key={index} className="bg-gradient-to-br from-navy-50 to-gold-50/30 rounded-2xl p-6 text-center hover:shadow-lg hover:shadow-navy-100/50 transition-all duration-300 w-full sm:w-auto sm:min-w-[200px]">
                <GraduationCap className="w-8 h-8 text-brand-navy mx-auto mb-3" />
                <p className="text-navy-700 font-medium text-sm">{partner}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 bg-gradient-to-br from-brand-navy to-navy-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Stay Up to Date!
          </h2>
          <p className="text-navy-200 text-lg mb-10">
            Our new DC Men Newsletter highlights recent professional recognitions and promotions within our community. Stay informed and inspired by the accomplishments of your peers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold px-8 shadow-xl transition-all duration-300">
                Subscribe to Newsletter
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/conference">
              <Button size="lg" variant="outline" className="border-2 border-white/30 text-white font-semibold px-8 hover:bg-white/10">
                View Conference
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
            Step Up with DC Men
          </h2>
          <p className="text-lg text-navy-600 mb-10">
            Take your career to new heights! Join our community and benefit from the wealth of resources and opportunities we provide.
          </p>
          <Link to="/membership">
            <Button size="lg" className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold px-8 shadow-xl shadow-gold-500/25">
              Join Our Community
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
